import type { CustomBlockTypeEnum } from 'venn-utils';
import type { InfoGraphicTypeEnum } from 'venn-api';

const ALWAYS_EXPANDABLE: ReadonlySet<CustomBlockTypeEnum> = new Set<CustomBlockTypeEnum>([
  'RETURNS_GRID',
  'PEER_GROUPS',
  'PORTFOLIO_COMPARISON',
  'PORTFOLIO_CONTRIBUTION',
  'SCENARIO',
  'FACTOR_CONTRIBUTION',
  'FACTOR_EXPOSURES',
  'PORTFOLIO_FACTOR_CONTRIBUTION',
  'OPTIONS_PERFORMANCE_SUMMARY',
  'CREDIT_BREAKDOWN',
  'DURATION_BREAKDOWN',
  'FINANCIAL_PERFORMANCE_INDICATORS',
  'MATURITY_BREAKDOWN',
  'TOP_HOLDINGS',
  'PERFORMANCE_SUMMARY_HISTORICAL',
  'PERFORMANCE_SUMMARY_HISTORICAL_PORTFOLIO_CONTRIBUTION',
  'PRIVATE_CAPITAL_SUMMARY',
  'TAX_IMPACT',
  'CORRELATION',
]);

const GRID_ONLY_EXPANDABLE: ReadonlySet<CustomBlockTypeEnum> = new Set<CustomBlockTypeEnum>([
  'NOTABLE_PERIODS',
  'ASSET_EXPOSURE',
  'GEOGRAPHY_EXPOSURE',
  'PRIVATE_CASH_FLOW',
  'PRIVATE_PERFORMANCE_SUMMARY',
  'PRIVATE_PERFORMANCE_TIME_SERIES',
  'PUBLIC_PRIVATE_ASSET_GROWTH_PERCENTILES',
  'PUBLIC_PRIVATE_ASSET_GROWTH_BREAKDOWN',
]);

const GRID_OR_BAR_EXPANDABLE: ReadonlySet<CustomBlockTypeEnum> = new Set<CustomBlockTypeEnum>(['SECTOR_EXPOSURE']);

const GRID_OR_PIVOTED_EXPANDABLE: ReadonlySet<CustomBlockTypeEnum> = new Set<CustomBlockTypeEnum>([
  'PERFORMANCE_SUMMARY',
]);

const HEATMAP_EXPANDABLE: ReadonlySet<CustomBlockTypeEnum> = new Set<CustomBlockTypeEnum>([
  'FACTOR_CONTRIBUTION_TREND',
]);

/**
 * Block types whose continuation slices render as a fixed-height, row-virtualized AG-Grid window
 * instead of a clipped full-content copy. Only safe while a row's position is predictable without
 * rendering the rows above it — i.e. uniform row heights. Every other expandable grid sets
 * `autoHeight: true` on at least one column, which makes row height content-driven and would let a
 * window drift out of alignment with the slice it was cut for. Setting `autoHeight` on a Tax Impact
 * column would break this the same way.
 */
const VIRTUALIZED_SLICE_EXPANDABLE: ReadonlySet<CustomBlockTypeEnum> = new Set<CustomBlockTypeEnum>(['TAX_IMPACT']);

export type BlockTypeArgs = {
  customBlockType: CustomBlockTypeEnum | undefined;
  infoGraphicType: InfoGraphicTypeEnum | undefined;
};

export const isExpandableBlockType = ({ customBlockType, infoGraphicType }: BlockTypeArgs): boolean => {
  if (!customBlockType) return false;
  if (ALWAYS_EXPANDABLE.has(customBlockType)) return true;
  if (GRID_ONLY_EXPANDABLE.has(customBlockType)) return infoGraphicType === 'GRID';
  if (GRID_OR_PIVOTED_EXPANDABLE.has(customBlockType)) {
    return infoGraphicType === 'GRID' || infoGraphicType === 'PIVOTED_GRID';
  }
  if (GRID_OR_BAR_EXPANDABLE.has(customBlockType)) return infoGraphicType === 'GRID' || infoGraphicType === 'BAR';
  if (HEATMAP_EXPANDABLE.has(customBlockType)) return infoGraphicType === 'HEATMAP';
  return false;
};

export const isCustomSliceExpandableBlockType = ({ customBlockType, infoGraphicType }: BlockTypeArgs): boolean => {
  if (!customBlockType) return false;
  if (GRID_OR_BAR_EXPANDABLE.has(customBlockType)) return infoGraphicType === 'BAR';
  if (HEATMAP_EXPANDABLE.has(customBlockType)) return infoGraphicType === 'HEATMAP';
  return false;
};

export const isVirtualizedSliceBlockType = ({ customBlockType }: BlockTypeArgs): boolean => {
  if (!customBlockType) return false;
  return VIRTUALIZED_SLICE_EXPANDABLE.has(customBlockType);
};
